"use client";

import { useState, useCallback } from "react";
import { signIn } from "next-auth/react";
import Icon from "@/components/ui/AppIcon";
import SocialLoginButtons from "./SocialLoginButtons";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";

interface AccountCreationFormProps {
  onComplete: (data: {
    email: string;
    name: string;
    password: string;
    method: string;
    isSignIn?: boolean;
    userId?: number;
    currentPlan?: string;
    tagsQuota?: number;
  }) => void;
  onGuestContinue: () => void;
}

function validateEmail(email: string): string {
  if (!email) return "Email is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    // Suggest common typo fixes
    if (email.includes("@gmial.")) return 'Did you mean @gmail.com?';
    if (email.includes("@gmai.")) return 'Did you mean @gmail.com?';
    if (email.includes("@yaho.")) return 'Did you mean @yahoo.com?';
    return "Please enter a valid email address";
  }
  return "";
}

export default function AccountCreationForm({ onComplete, onGuestContinue }: AccountCreationFormProps) {
  const [mode, setMode] = useState<"create" | "signin">("create");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const getFieldStatus = (field: string, value: string) => {
    if (!touched[field] || !value) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  const validateField = useCallback((field: string, value: string) => {
    const newErrors = { ...errors };
    switch (field) {
      case "email":
        const emailErr = validateEmail(value);
        if (emailErr) newErrors.email = emailErr;
        else delete newErrors.email;
        break;
      case "password":
        if (!value) newErrors.password = "Password is required";
        else if (value.length < 8) newErrors.password = "Password must be at least 8 characters";
        else delete newErrors.password;
        break;
      case "confirmPassword":
        if (value !== password) newErrors.confirmPassword = "Passwords do not match";
        else delete newErrors.confirmPassword;
        break;
      case "name":
        if (!value.trim()) newErrors.name = "Name is required";
        else delete newErrors.name;
        break;
    }
    setErrors(newErrors);
  }, [errors, password]);

  const handleBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Touch all fields
    const allTouched = { email: true, password: true, name: true, confirmPassword: mode === "create" };
    setTouched(allTouched);

    const emailErr = validateEmail(email);
    const newErrors: Record<string, string> = {};
    if (emailErr) newErrors.email = emailErr;
    if (!password || password.length < 8) newErrors.password = "Password must be at least 8 characters";
    if (mode === "create" && !name.trim()) newErrors.name = "Name is required";
    if (mode === "create" && password !== confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (mode === "create" && !agreed) newErrors.agreed = "Please agree to the terms";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    // If signing in, verify credentials first
    if (mode === "signin") {
      try {
        // Call cloud service to verify credentials
        const cloudServiceUrl = process.env.NEXT_PUBLIC_CLOUD_SERVICE_URL || 'http://localhost:5000';
        const response = await fetch(`${cloudServiceUrl}/api/verify-credentials`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setErrors({ ...newErrors, password: data.error || 'Invalid email or password' });
          setLoading(false);
          return;
        }

        // Credentials verified, pass user info including plan details
        onComplete({
          email,
          name: data.name || email,
          password,
          method: "email",
          isSignIn: true,
          userId: data.user_id,
          currentPlan: data.plan_id,
          tagsQuota: data.tags_quota
        });
      } catch (err: any) {
        console.error('Sign-in error:', err);
        setErrors({ ...newErrors, password: 'Unable to verify credentials. Please try again.' });
        setLoading(false);
        return;
      }
    } else {
      // Creating new account
      onComplete({ email, name, password, method: "email" });
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signIn("google", {
        redirect: false,
        callbackUrl: window.location.href,
      });

      if (result?.ok) {
        // Fetch user data from session
        const response = await fetch("/api/auth/session");
        const session = await response.json();

        if (session?.user) {
          onComplete({
            email: session.user.email || "",
            name: session.user.name || "",
            password: "", // OAuth users don't need password
            method: "google",
          });
        }
      } else {
        // Check if it's a configuration error
        const errorMessage = result?.error || "";
        if (errorMessage.includes("Configuration") || errorMessage.includes("CLIENT_ID")) {
          setErrors({
            google: "Google sign-in is not configured yet. Please use email to continue, or ask the site owner to set up Google OAuth (see GOOGLE_OAUTH_QUICKSTART.md)."
          });
        } else {
          setErrors({ google: "Sign in failed. Please try email instead." });
        }
        console.error("Google sign-in failed:", result?.error);
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      // Check if it's likely a missing credentials error
      if (error?.message?.includes("fetch") || error?.message?.includes("Configuration")) {
        setErrors({
          google: "Google sign-in is not set up yet. Please use email to continue."
        });
      } else {
        setErrors({ google: "An error occurred. Please try email instead." });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field: string, value: string) => {
    const status = getFieldStatus(field, value);
    const base = "w-full bg-bg-elevated text-white placeholder-gray-600 rounded-xl px-4 py-3.5 text-sm transition-all duration-300 outline-none pr-10";
    if (status === "error") return `${base} border border-red-500/60 shadow-[0_0_0_3px_rgba(239,68,68,0.08)]`;
    if (status === "success") return `${base} border border-accent-emerald/40 shadow-[0_0_0_3px_rgba(16,185,129,0.06)]`;
    return `${base} border border-white/8 focus:border-accent-crimson/40 focus:shadow-[0_0_0_3px_rgba(255,0,76,0.08)]`;
  };

  return (
    <div className="bg-bg-card border border-white/8 rounded-3xl p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-white mb-1">
          {mode === "create" ? "Create your account" : "Welcome back"}
        </h2>
        <p className="text-gray-500 text-sm">
          {mode === "create" ? "Join TapTale to manage your order and collection" : "Sign in to continue with your purchase"}
        </p>
      </div>

      {/* Social Login */}
      <SocialLoginButtons
        loading={loading}
        onGoogleClick={handleGoogleSignIn}
        onAppleClick={() => onComplete({ email: "apple@user.com", name: "Apple User", method: "apple" })}
        onFacebookClick={() => onComplete({ email: "fb@user.com", name: "Facebook User", method: "facebook" })}
      />

      {/* Google Error Message */}
      {errors.google && (
        <div className="mt-3 bg-accent-gold/10 border border-accent-gold/20 rounded-xl p-3 flex items-start gap-2">
          <Icon name="InformationCircleIcon" size={16} className="text-accent-gold flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-accent-gold text-xs font-medium mb-1">Google Sign-In Not Available</p>
            <p className="text-gray-400 text-xs">Please use email/password below to continue with your order.</p>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-white/8" />
        <span className="text-gray-600 text-xs font-mono">or</span>
        <div className="flex-1 h-px bg-white/8" />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          {/* Name (create only) */}
          {mode === "create" && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (touched.name) validateField("name", e.target.value);
                  }}
                  onBlur={(e) => handleBlur("name", e.target.value)}
                  placeholder="Jan Kowalski"
                  autoComplete="name"
                  className={inputClass("name", name)}
                  aria-label="Full name"
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {getFieldStatus("name", name) === "success" && (
                  <Icon name="CheckCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-accent-emerald" />
                )}
                {getFieldStatus("name", name) === "error" && (
                  <Icon name="ExclamationCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-400" />
                )}
              </div>
              {errors.name && touched.name && (
                <p id="name-error" className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} /> {errors.name}
                </p>
              )}
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (touched.email) validateField("email", e.target.value);
                }}
                onBlur={(e) => handleBlur("email", e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                className={inputClass("email", email)}
                aria-label="Email address"
                aria-describedby={errors.email ? "email-error" : "email-hint"}
              />
              {getFieldStatus("email", email) === "success" && (
                <Icon name="CheckCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-accent-emerald" />
              )}
              {getFieldStatus("email", email) === "error" && (
                <Icon name="ExclamationCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-400" />
              )}
            </div>
            {errors.email && touched.email ? (
              <p id="email-error" className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} /> {errors.email}
              </p>
            ) : (
              <p id="email-hint" className="text-gray-600 text-[11px] mt-1.5">We&apos;ll never share your email</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (touched.password) validateField("password", e.target.value);
                }}
                onBlur={(e) => handleBlur("password", e.target.value)}
                placeholder="Min. 8 characters"
                autoComplete={mode === "create" ? "new-password" : "current-password"}
                className={`${inputClass("password", password)} pr-20`}
                aria-label="Password"
                aria-describedby={errors.password ? "password-error" : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <Icon name={showPassword ? "EyeSlashIcon" : "EyeIcon"} size={16} />
              </button>
            </div>
            {errors.password && touched.password && (
              <p id="password-error" className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} /> {errors.password}
              </p>
            )}
            {mode === "create" && <PasswordStrengthIndicator password={password} />}
          </div>

          {/* Confirm Password (create only) */}
          {mode === "create" && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (touched.confirmPassword) validateField("confirmPassword", e.target.value);
                  }}
                  onBlur={(e) => handleBlur("confirmPassword", e.target.value)}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  className={inputClass("confirmPassword", confirmPassword)}
                  aria-label="Confirm password"
                />
                {getFieldStatus("confirmPassword", confirmPassword) === "success" && (
                  <Icon name="CheckCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-accent-emerald" />
                )}
                {getFieldStatus("confirmPassword", confirmPassword) === "error" && (
                  <Icon name="ExclamationCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-400" />
                )}
              </div>
              {errors.confirmPassword && touched.confirmPassword && (
                <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} /> {errors.confirmPassword}
                </p>
              )}
            </div>
          )}

          {/* Terms (create only) */}
          {mode === "create" && (
            <div>
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      if (errors.agreed) setErrors(prev => { const n = {...prev}; delete n.agreed; return n; });
                    }}
                    className="sr-only"
                    aria-label="Agree to terms and privacy policy"
                  />
                  <div className={`w-5 h-5 rounded-md border transition-all duration-200 flex items-center justify-center ${
                    agreed
                      ? "bg-accent-crimson border-accent-crimson"
                      : errors.agreed
                      ? "border-red-500/60 bg-red-500/5" :"border-white/20 bg-bg-elevated group-hover:border-white/30"
                  }`}>
                    {agreed && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to TapTale&apos;s{" "}
                  <a href="#" className="text-gray-300 hover:text-white underline underline-offset-2 transition-colors">Terms of Service</a>
                  {" "}and{" "}
                  <a href="#" className="text-gray-300 hover:text-white underline underline-offset-2 transition-colors">Privacy Policy</a>
                </span>
              </label>
              {errors.agreed && (
                <p className="text-red-400 text-xs mt-1.5 ml-8">{errors.agreed}</p>
              )}
            </div>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-6 flex items-center justify-center gap-2.5 bg-gradient-to-r from-accent-crimson to-accent-crimson-hover text-white font-semibold text-sm py-4 rounded-full shadow-[0_12px_32px_rgba(255,0,76,0.35)] hover:shadow-[0_16px_40px_rgba(255,0,76,0.45)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          aria-label={mode === "create" ? "Create account and continue" : "Sign in and continue"}
        >
          {loading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={15} />
              <span>{mode === "create" ? "Create Account & Continue" : "Sign In & Continue"}</span>
            </>
          )}
        </button>
      </form>

      {/* Footer links */}
      <div className="mt-5 flex flex-col items-center gap-3">
        <button
          onClick={() => setMode(mode === "create" ? "signin" : "create")}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          {mode === "create" ? "Already have an account? Sign in" : "Don't have an account? Create one"}
        </button>
        <button
          onClick={onGuestContinue}
          className="text-xs text-gray-600 hover:text-gray-400 transition-colors flex items-center gap-1"
        >
          Continue as guest <Icon name="ArrowRightIcon" size={11} />
        </button>
      </div>
    </div>
  );
}
